import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "@/hooks/useProject";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, BarChart3, ExternalLink } from "lucide-react";
import { useState } from "react";
import { ChartConfigDialog } from "./ChartConfigDialog";
import { ChartPreviewDialog } from "./ChartPreviewDialog";
import { ChartPreviewInline } from "./ChartPreviewInline";

interface ChartManagerProps {
  templateId: number;
  jotformFormId?: string | null;
  jotformFormIdReferral?: string | null;
}

export function ChartManager({ templateId, jotformFormId, jotformFormIdReferral }: ChartManagerProps) {
  const { projectClient } = useProject();
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingChart, setEditingChart] = useState<any | null>(null);
  const [previewChart, setPreviewChart] = useState<any | null>(null);

  // Fetch charts for this template
  const { data: charts = [], isLoading } = useQuery({
    queryKey: ["template_charts", templateId],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from("template_charts")
        .select("*")
        .eq("template_id", templateId)
        .eq("project_id", projectClient.getProjectId())
        .order("display_order");

      if (error) throw error;
      return data || [];
    },
    enabled: !!templateId,
  });

  // Delete chart mutation
  const deleteMutation = useMutation({
    mutationFn: async (chartId: number) => {
      const { error } = await projectClient
        .from("template_charts")
        .delete()
        .eq("id", chartId)
        .eq("project_id", projectClient.getProjectId());

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Gráfico eliminado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["template_charts", templateId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al eliminar gráfico");
    },
  });

  const handleEdit = (chart: any) => {
    setEditingChart(chart);
    setCreateDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setEditingChart(null);
  };

  if (isLoading) {
    return <div className="p-4">Cargando gráficos...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Gráficos de la Plantilla</h2>
          <p className="text-sm text-muted-foreground">
            Configura los gráficos que se insertarán en el PowerPoint
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Agregar Gráfico
        </Button>
      </div>

      {charts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              No hay gráficos configurados para esta plantilla
            </p>
            <Button onClick={() => setCreateDialogOpen(true)} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Crear primer gráfico
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {charts.map((chart) => (
            <Card key={chart.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{chart.name}</CardTitle>
                    <CardDescription>
                      {chart.chart_type.toUpperCase()} - Slide {chart.slide_number}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(chart)}
                    >
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        if (confirm("¿Estás seguro de eliminar este gráfico?")) {
                          deleteMutation.mutate(chart.id);
                        }
                      }}
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Left side: Chart info */}
                  <div className="text-sm text-muted-foreground space-y-1">
                    <div>Posición: ({chart.position_x}, {chart.position_y})</div>
                    <div>Tamaño: {chart.width} × {chart.height}</div>
                    {chart.description && <div className="mt-2">{chart.description}</div>}
                  </div>

                  {/* Right side: Inline preview */}
                  <div>
                    <ChartPreviewInline
                      chart={chart}
                      onExpand={() => setPreviewChart(chart)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ChartConfigDialog
        open={createDialogOpen}
        onOpenChange={handleCloseDialog}
        templateId={templateId}
        chart={editingChart}
        jotformFormId={jotformFormId}
        jotformFormIdReferral={jotformFormIdReferral}
      />

      <ChartPreviewDialog
        open={!!previewChart}
        onOpenChange={(open: boolean) => !open && setPreviewChart(null)}
        chart={previewChart}
      />
    </div>
  );
}
